import { useState, useEffect, useRef, useCallback } from 'react';
import { fetchPaginatedContacts } from '../services/contactListService';
import { useAuth } from '../../../../../contexts/AuthContext';

const useContactsList = (selectedListType, searchQuery = '') => {
  const { currentUser } = useAuth();
  const rowsPerPage = currentUser?.rowsPerPage ?? 25;
  const [contacts, setContacts] = useState([]);
  const [contactFields, setContactFields] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 0, size: rowsPerPage, totalPages: 0 });
  const [dataVersion, setDataVersion] = useState(0);
  const fetchController = useRef(null);

  const fetchContacts = useCallback(async () => {
    if (fetchController.current) {
      fetchController.current.abort();
    }

    fetchController.current = new AbortController();
    const signal = fetchController.current.signal;

    try {
      setIsLoading(true);

      if (['allContacts', 'managementCompanies', 'customers', 'buildings', 'employees'].includes(selectedListType)) {
        const { data, fields, totalPages } = await fetchPaginatedContacts(
          selectedListType,
          pagination.page,
          pagination.size,
          signal,
          searchQuery
        );
        setContacts(data);
        setContactFields(fields);
        setPagination((prev) => ({ ...prev, totalPages }));
      } else {
        setContacts([]);
        setContactFields([]);
        setPagination((prev) => ({ ...prev, totalPages: 0 }));
      }

      setIsLoading(false);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching contacts:', error);
      }
      setContacts([]);
      setContactFields([]);
      setIsLoading(false);
    }
  }, [selectedListType, pagination.page, pagination.size, searchQuery, dataVersion]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 0 }));
  }, [selectedListType, searchQuery]);

  useEffect(() => {
    fetchContacts();
    return () => {
      if (fetchController.current) {
        fetchController.current.abort();
      }
    };
  }, [fetchContacts]);

  return {
    contacts,
    contactFields,
    isLoading,
    pagination,
    setPagination,
    setDataVersion,
  };
};

export default useContactsList;
